"use client";

import React, { useRef, useState, useEffect } from "react";
import { posts as allPosts } from "@/app/data/blogPostData";
import { sortPostsByDate } from "@/app/shared/utils/utils";
import PageWrapper from "../PageWrapper/PageWrapper";
import TopSection from "@/app/shared/components/TopSection/TopSection";
import BlogPostCard from "../BlogPostCard/BlogPostCard";
import BackwardsArrowLink from "@/app/shared/components/BackwardsArrowLink/BackwardsArrowLink";

const Blog = () => {
  const posts = sortPostsByDate(allPosts);
  const initialBlogsToShow = 6;
  const postRef = useRef<HTMLDivElement | null>(null);
  const observer = useRef<IntersectionObserver | null>(null);

  const [blogsToShow, setBlogsToShow] = useState(initialBlogsToShow);

  const handleObserver = (entries: IntersectionObserverEntry[]) => {
    const target = entries[0];
    if (target.isIntersecting) {
      const nextBlogsToShow = blogsToShow + initialBlogsToShow;
      const totalBlogs = posts.length;
      const updatedBlogsToShow = Math.min(nextBlogsToShow, totalBlogs);
      setBlogsToShow(updatedBlogsToShow);
    }
  };

  useEffect(() => {
    if (postRef.current) {
      observer.current = new IntersectionObserver(handleObserver, {
        root: null,
        rootMargin: "0px",
        threshold: 1.0,
      });
      observer.current.observe(postRef.current);
    }

    return () => {
      if (observer.current) {
        observer.current.disconnect();
      }
    };
    // this is necessary as handleObserver is not needed in the dependency array
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blogsToShow]);

  return (
    <PageWrapper>
      <TopSection />
      <div className="mx-auto min-h-screen max-w-screen-xl px-6 py-12 md:px-12 md:py-20 lg:px-24 lg:py-0">
        <div className="lg:py-24">
          <BackwardsArrowLink href="/" text="Martin Beck Andersen"/>
          <h1 className="text-4xl font-bold tracking-tight text-slate-200 sm:text-5xl">
            All Posts
          </h1>
          <div className="mt-12 grid gap-5 max-w-lg mx-auto lg:grid-cols-3 lg:max-w-none">
            {posts.slice(0, blogsToShow).map((post, index) => (
              <React.Fragment key={post.id}>
                <BlogPostCard idx={index} {...post} />
                {index === blogsToShow - 1 && <div ref={postRef}></div>}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
    </PageWrapper>
  );
};

export default Blog;
